/* eslint-disable react/no-array-index-key */
/* eslint-disable max-len */
import React, {
    useContext, useRef, useState, useEffect,
} from 'react';
import classnamesBind from 'classnames/bind';
import PropTypes from 'prop-types';
import Icon from '@concur/nui-widgets/Icon';
import IconButton from '@concur/nui-widgets/lib/IconButton/IconButton';
import Spinner from '@concur/nui-widgets/Spinner';
import useQuerySelector from '@concur/nui-widgets/lib/utils/useQuerySelector';
import { withFormatter } from '@concur/nui-intl-runtime';
import { compose, lowerCase, withErrorBoundary } from '@concur/core-ui-shell';
import useWindowEventCallback from '@concur/core-ui-shell/lib/utils/useWindowEventCallback';
import NavMenu from '@concur/nui-widgets/NavMenu';
import addScriptToDOM from '@concur/core-ui-shell/lib/utils/addScriptToDOM';
import useModifiedHelpMenu from '../utils/useModifiedHelpMenu';
import LanguageDropdown from './_LanguageDropdown';
import MenuItem from './_MenuItem';
import MenuItemToggle from './_MenuItemToggle';
import UserAvatar from './_UserAvatar';
import SideNav from './_SideNav';
import AppHeaderContext from './_AppHeaderContext';
import ExpandedSideNav from './_ExpandedSideNav';
import UserProfileMenu from '../UserProfileMenu/UserProfileMenu';
import Logo from '../Logo/Logo';
import { getProfileDisplayText } from '../UserProfileMenu/_ProfileIdentifier';
import useDisplacedTargets from '../utils/useDisplacedTargets';
import AboutDialog from '../UserProfileMenu/_AboutDialog';
import useElementBottom from '../utils/useElementBottom';
import styles from './Shellbar.css';
import FallbackComponent from '../FallbackComponent/_FallbackComponent';
import DigitalAssistantMenuItem from './_DigitalAssistantMenuItem';
import HelpPortalMenuItem from './_HelpPortalMenuItem';
import MessageCenterMenuItem from './_MessageCenterMenuItem';
import TranslationToolMenuItem from './_TranslationToolMenuItem';
import NavLogo from '../Logo/_NavLogo';
import useActiveMenuPath from '../utils/useActiveMenuPath';
import addPageMenuItemsToActiveLeaf from '../utils/addPageMenuItemsToActiveLeaf';
import { getSideNavState, setSideNavState } from '../utils/sideNavStateUtils';
import useShellbarSize from './useShellbarSize';
import { isHighContrastTheme } from '../utils/themeUtils';
import { getSidenavItemAnalyticsJson } from '../utils/getSidenavItemAnalyticsJson';

const CSS_BLOCK = 'sapcnqr-shellbar';

const getAdjustedMenuData = (menuData, formatter) => {
    const { helpMenuItem, supportMenuItem, ...otherMenuData } = menuData || {};

    const helpMenuItemSubItems = [...(helpMenuItem?.subItems || [])];

    if (supportMenuItem) {
        helpMenuItemSubItems.push({
            ...supportMenuItem,
            // when moving support into the help menu, it changes label and id values
            name: formatter.formattedMessage({ id: 'CoreUI.contactSupport' }),
            id: 'ContactSupport',
        });
    }

    const adjustedMenuData = {
        ...otherMenuData,
        helpMenuItem: {
            ...helpMenuItem,
            subItems: helpMenuItemSubItems,
        },
    };
    return addPageMenuItemsToActiveLeaf(adjustedMenuData);
};
// Remove unused props in the future once all teams have upgraded to min version of
// nui-shell that stops passing these props + SU251 is in production
const Shellbar = (props) => {
    const {
        actingAs = {},
        baseUrl,
        className,
        formatter,
        isLoggedIn,
        lang,
        menuData,
        showLanguageDropdown,
        aboutDemo,
        userProfileMenuProps = {},
        isOpusUser,
        themeName,
        configOptions,
        brandingLogoUrl,
        brandingLogoData,
        companyName: companyNameProp,
        oshCacheIdentifier,
        /* eslint-disable no-unused-vars */
        isGov,
        isNuiHome,
        onEndSession,
        onStartSession,
        logoutUser,
        cdsURL,
        /* eslint-enable no-unused-vars */
    } = props;

    const {
        completeEnabled = false,
    } = configOptions || {};

    const adjustedMenuData = getAdjustedMenuData(menuData, formatter);

    const { homeMenuItem, profileMenuItem, helpMenuItem } = adjustedMenuData;
    const classnames = classnamesBind.bind(styles);

    const { isLocalizationUser } = useContext(AppHeaderContext) || {};
    const { employeeDisplayName } = useContext(AppHeaderContext) || {};

    const {
        partnerLogo,
        lastSigninDateString,
        isProfessional,
        isStandard,
        ...otherUserProfileMenuProps
    } = userProfileMenuProps || {};

    const [showSpinner, setShowSpinner] = useState(false);
    const [showAboutDialog, setShowAboutDialog] = useState(aboutDemo);
    const [showUserProfileMenu, setShowUserProfileMenu] = useState(false);
    const [showSideNavContent, setShowSideNavContent] = useState(false);
    const [sideNavExpanded, setSideNavExpanded] = useState(() => getSideNavState());
    const [sideNavCollapsedOverlay, setSideNavCollapsedOverlay] = useState(false);
    const [sideNavOverlay, setSideNavOverlay] = useState(false);
    const [showSideNavOverlay, setShowSideNavOverlay] = useState(false);
    const [helpPortalLoaded, setHelpPortalLoaded] = useState(false);
    const [translationToolLoaded, setTranslationToolLoaded] = useState(false);
    const [currentLayoutSidePanelElementRefs, setCurrentLayoutSidePanelElementRefs] = useState({});
    const sidenavInitializedRef = useRef(false);
    const hasSideNav = adjustedMenuData?.applicationMenuItems?.length > 0
        || adjustedMenuData?.adminMenuItem?.length > 0
        || adjustedMenuData?.adminMenuItems?.length > 0
        || adjustedMenuData?.linksMenuItem?.length > 0;

    const headerBottom = useElementBottom('#sapCnqrHeaderContainer');
    const testEntityBannerBottom = useElementBottom('#sapCnqrTestEntityBannerContainer', 0);

    const getExtraHeight = () => {
        if (typeof document === 'undefined' || typeof window === 'undefined') {
            return 0;
        }
        const bannerFrame = document.querySelector('#sapCnqrTestEntitySiteBanner');
        if (!bannerFrame) return 0;
        const computedStyle = window.getComputedStyle(bannerFrame);
        const borderBottom = computedStyle.getPropertyValue('border-bottom-width') ?? 0;
        const marginBottom = computedStyle.getPropertyValue('margin-bottom-width') ?? 0;

        return parseInt(borderBottom + marginBottom, 10);
    };

    const extraHeight = getExtraHeight();

    useEffect(() => {
        setSideNavState(sideNavExpanded);
    }, [sideNavExpanded]);

    useEffect(() => {
        if (showAboutDialog) {
            setShowUserProfileMenu(false);
        }
    }, [showAboutDialog]);

    useEffect(() => {
        if (!sidenavInitializedRef.current && showSideNavContent) {
            sidenavInitializedRef.current = true;
            document.dispatchEvent(new CustomEvent('ui-floorplans-sidenav-loaded', {
                bubbles: true,
            }));
        }
    }, [showSideNavContent, sidenavInitializedRef.current]);

    const hasFlexibleLayoutSidePanel = document.querySelectorAll('[data-ui-floorplans="overlay-only"]').length > 0;
    const hasEXTJS = document.querySelectorAll('[data-ui-floorplans="overlay-collapsed-only"]').length > 0;
    const appContent = useQuerySelector('#cnqr-app-content');

    // extra spacer cleanup for overlay-only
    useEffect(() => {
        if (hasFlexibleLayoutSidePanel) {
            setTimeout(() => {
                const spacerElement = document.getElementById('sapcnqrSideNavSpacer');
                if (spacerElement) {
                    spacerElement.remove();
                }
            }, 2000);
        }
    }, [appContent, hasFlexibleLayoutSidePanel]);

    useShellbarSize({
        hasEXTJS,
        hasFlexibleLayoutSidePanel,
        // only overlay
        onSmallScreen() {
            setSideNavOverlay(true);
            setSideNavCollapsedOverlay(false);
        },
        // overlay and collapsed only
        onMediumScreen() {
            setSideNavOverlay(true);
            setSideNavCollapsedOverlay(true);
            setSideNavExpanded(false);
        },
        // expanded and collapsed
        onLargeScreen() {
            setSideNavOverlay(false);
            setSideNavCollapsedOverlay(false);
        },
    });

    const getDisplacement = () => {
        if (sideNavOverlay && !sideNavCollapsedOverlay) {
            return '0px';
        }
        return sideNavExpanded ? '256px' : '64px';
    };

    // handles LayoutSidePanelElements in fullscreen mode or flexible layouts with
    // multiple side panels visible
    // (custom 'nui-layouts-side-panel' event
    // is fired from nui-layouts code)
    useWindowEventCallback(('nui-layouts-side-panel'), (e) => {
        const detail = e?.detail || {};
        const el = !!detail?.ref?.current;
        if (el) {
            setCurrentLayoutSidePanelElementRefs((prev) => ({
                ...prev,
                [detail.name]: {
                    ref: detail.ref,
                    isFullscreen: !!detail?.isFullscreen,
                    open: detail?.open,
                    originalWidth: detail?.originalWidth,
                },
            }));
        }
    });

    useDisplacedTargets(
        {
            disable: !isLoggedIn || hasFlexibleLayoutSidePanel,
            targetSelectorArray: ['#root', '#quicktasks', '[data-ui-floorplans="feature-banner"]'],
            legacyOnlyTargetSelectorArray: ['#cnqr-app-content'],
            currentLayoutSidePanelElementRefs,
            displacement: getDisplacement(),
            onDisplaced: () => setShowSideNavContent(true),
            // after sidenav is in production,
            // these should be added to global styles to prevent inline style csp errors
            targetStyles: {
                '#root': {
                    flex: 1,
                    minWidth: 0,
                },
            },
            additionalLegacyStyles: {
                styles: {
                    // 12 cols
                    '.container': {
                        width: '100%',
                        flex: `1 1 ${getDisplacement()}`,
                        minWidth: 'unset',
                        // needed for pages like UI/administration/ServiceLogs
                        overflowX: 'auto',
                    },
                    // 12 cols - fluid
                    '.container-fluid': {
                        width: '100%',
                        flex: `1 1 ${getDisplacement()}`,
                        minWidth: 'unset',
                    },
                    // admin/request/requestAdmin.asp
                    // TREX expense pages with feature banner
                    '.feature-banner': {
                        width: '100%',
                    },
                },
                conditionalStyles: {
                    '.container': {
                        style: {
                            overflowX: 'visible',
                        },
                        condition: document
                            .querySelector('body')
                            .classList.contains('cnqr-grid-wide'),
                    },
                },
                // css added in global styles via manifest-cnqr.less
                // source in: less/cnqr-global/_components/_cnqr-sidenav.less
                // adds classes in className key based on condition to the element key
                classes: {
                    '#sidebar1': [
                        {
                            condition: document
                                .querySelector('body')
                                .classList.contains('cnqr-grid-sidebar-fixed-narrow'),
                            className: 'sapcnqr-sidenav-override__sidebar1--narrow',
                        },
                    ],
                    '#featured': [
                        {
                            condition: document
                                .querySelector('body')
                                .classList.contains('cnqr-grid-sidebar-fixed-narrow'),
                            className: 'sapcnqr-sidenav-override__featured--narrow',
                        },
                    ],
                },
            },
        },
        [appContent],
    );

    const classes = classnames(
        CSS_BLOCK,
        {
            [`${CSS_BLOCK}--complete`]: ((isOpusUser && isNuiHome) || (completeEnabled && isOpusUser)) && !isHighContrastTheme(themeName),
        },
        className,
    );

    const headerClasses = classnames(`${CSS_BLOCK}__header`, `${CSS_BLOCK}__header--primary`, {
        [`${CSS_BLOCK}__header--sidenav`]: hasSideNav,
    });

    const overlayClasses = classnames({
        [`${CSS_BLOCK}__overlay`]: !hasFlexibleLayoutSidePanel && !hasEXTJS,
        [`${CSS_BLOCK}__overlay--side-nav`]: sideNavOverlay,
    });

    const homeIconClasses = classnames(`${CSS_BLOCK}__home-icon`);

    const { allUsers, companyName, otherUserName } = actingAs;
    const isActingForOthers = !!(allUsers || companyName || otherUserName);

    // we want to exclude high contrast themes
    // this is a way of checking if the complete theme is enabled via configOptions.completeEnabled in xpd for ui-theming
    // themeName will come through as sap_horizon or sap_horizon_dark in production, complete and complete_dark are
    // here to support playground environments
    const isCompleteTheme = (theme) => (theme === 'complete' || theme === 'complete_dark')
        || ((theme === 'sap_horizon' || theme === 'sap_horizon_dark') && isOpusUser);

    const generateProfileButton = () => {
        const profileDisplayText = getProfileDisplayText({
            actingAs,
            formatter,
            employeeDisplayName,
            shortNames: true,
        });

        if (isActingForOthers) {
            return (
                <>
                    <Icon
                        ariaHidden
                        data-test="shellbar-acting-as-other"
                        className={classnames(`${CSS_BLOCK}__acting-as-icon`)}
                        iconName="user-admin"
                    />
                    {profileDisplayText}
                </>
            );
        }

        return (
            <UserAvatar
                className={classnames(`${CSS_BLOCK}__avatar`)}
                formatter={formatter}
                lang={lang}
                employeeDisplayName={employeeDisplayName}
                size="xs"
                isOpusUser={isOpusUser}
                isCompleteTheme={isCompleteTheme(themeName)}
            />
        );
    };

    const handleStartLoading = () => {
        setShowSpinner(true);
    };

    const handleEndLoading = () => {
        setShowSpinner(false);
    };

    useModifiedHelpMenu({
        menuData: adjustedMenuData,
        translationToolLoaded,
        helpPortalLoaded,
    });

    const homeLogoDescriptionId = `${`logo-description-${homeMenuItem?.id || 'home'}`.trim()}`;

    const navLogo = (
        <NavLogo
            styles={styles}
            cssBlock={CSS_BLOCK}
            className={homeIconClasses}
            descriptionId={homeLogoDescriptionId}
            isOpusUser={isOpusUser}
            brandingLogoUrl={brandingLogoUrl}
            brandingLogoData={brandingLogoData}
            companyName={companyNameProp}
            configOptions={configOptions}
        />
    );

    const profileQuickHelpRef = useRef();
    const profileUserDropdownRef = useRef();
    // When adminMenuItems (array) is present, exclude adminMenuItem (single object) from
    // active path search to avoid it winning with a longer/deeper path. The adminMenuItems
    // items have different ids and the active path needs to match those, not the old ones.
    const activePathMenuData = adjustedMenuData?.adminMenuItems?.length > 0
        ? { ...adjustedMenuData, adminMenuItem: undefined }
        : adjustedMenuData;
    const activePathArray = useActiveMenuPath(activePathMenuData);
    const menuButtonRef = useRef();

    const shellbarProfileMenuId = 'shellbarProfileMenu';

    const profileOverlayProps = {
        onClickOutside: (event) => {
            if (
                profileQuickHelpRef?.current?.contains?.(event?.target)
                || profileUserDropdownRef?.current?.contains?.(event?.target)
                || (event?.target && event?.target?.closest?.(`#${shellbarProfileMenuId}`) !== null)
            ) {
                return true;
            }

            return false;
        },
        popperContentProps: {
            className: classnames(`${CSS_BLOCK}__overlay-profile-wrapper`),
        },
        id: shellbarProfileMenuId,
        className: classnames(`${CSS_BLOCK}__overlay-profile`, {
            [`${CSS_BLOCK}__overlay-profile--spinner-visible`]: showSpinner,
        }),
        showArrow: true,
        offset: 4,
    };

    const fallbackComp = (errorId) => (
        <FallbackComponent componentCssBlock={UserProfileMenu.cssBlock} correlationId={errorId} />
    );
    const { country, demo } = otherUserProfileMenuProps || {};

    // we have to add this script in the shellbar, and not inside UserProfileMenu,
    // so that cookie consent top bar is shown on page load. Otherwise, it is only shown when
    // the Legal Information item is expanded inside the UserProfileMenu.
    useEffect(() => {
        if (!demo) {
            if (window.truste?.eu?.icon) {
                window.truste.eu.icon.initialize();
            } else {
                // eslint-disable-next-line max-len
                const scriptUrl = `//consent.trustarc.com/notice?domain=concursolutions.com&c=teconsent&language=${lang}&js=nj&noticeType=bb&text=true&gtm=1${
                    country ? `&country=${country}` : ''
                }`;
                const attributes = {
                    type: 'text/javascript',
                    async: true,
                    crossorigin: 'anonymous',
                };

                addScriptToDOM(scriptUrl, attributes, 'sapcnqr-trustarc');
            }
        }
    }, []);

    return (
        <>
            <div className={classes}>
                {isLoggedIn ? (
                    <>
                        <a
                            className={classnames(`${CSS_BLOCK}__screen-reader-only`)}
                            href="#cnqr-app-content"
                        >
                            {formatter.formattedMessage({ id: 'CoreUI.skipNavigation' })}
                        </a>
                        {!sideNavOverlay && (
                            <a
                                className={classnames(`${CSS_BLOCK}__screen-reader-only`)}
                                href="#sideNavBar"
                            >
                                {formatter.formattedMessage({ id: 'CoreUI.sideNavSkipNavigation' })}
                            </a>
                        )}
                        <div className={headerClasses}>
                            {showSideNavContent
                                && hasSideNav
                                && !sideNavOverlay
                                && !sideNavCollapsedOverlay && (
                                <IconButton
                                    iconName="menu"
                                    className={classnames({
                                        [`${CSS_BLOCK}__button-outer`]: !isOpusUser || (isOpusUser && !isCompleteTheme(themeName)),
                                    })}
                                    innerProps={{
                                        className: classnames({
                                            [`${CSS_BLOCK}__button`]: !isOpusUser || (isOpusUser && !isCompleteTheme(themeName)),
                                        }),
                                    }}
                                    ariaLabel={formatter.formattedMessage({
                                        id: sideNavExpanded
                                            ? 'CoreUI.sideNav.collapse'
                                            : 'CoreUI.sideNav.expand',
                                    })}
                                    onClick={() => {
                                        setSideNavExpanded(!sideNavExpanded);
                                    }}
                                    ref={menuButtonRef}
                                    tooltipProps={{
                                        // do not use top, it interferes with testentity banner
                                        placement: ['right', 'bottom', 'left'],
                                    }}
                                    data-analytics-json={getSidenavItemAnalyticsJson(sideNavExpanded ? 'Collapse' : 'Expand')}
                                />
                            )}
                            <NavMenu.Bar
                                listProps={{ className: classnames(`${CSS_BLOCK}__nav-menu-bar`) }}
                                ariaLabel={formatter.formattedMessage({
                                    id: 'CoreUI.primaryMenuAriaLabel',
                                })}
                                className={classnames(`${CSS_BLOCK}__navigation`)}
                            >
                                <ul
                                    className={classnames(
                                        `${CSS_BLOCK}__menu`,
                                        `${CSS_BLOCK}__menu--primary`,
                                    )}
                                    ref={menuButtonRef}
                                >
                                    {sideNavOverlay && (
                                        <>
                                            {/* small viewport (< 599px) - expanded sidenav in overlay */}
                                            <MenuItemToggle
                                                styles={styles}
                                                cssBlock={CSS_BLOCK}
                                                tooltipMessage={formatter.formattedMessage({
                                                    id: 'CoreUI.applicationMenu',
                                                })}
                                                buttonProps={{
                                                    id: 'overlayToggleButton',
                                                }}
                                                overlayProps={{
                                                    className: overlayClasses,
                                                }}
                                                content={<Icon ariaHidden iconName="menu" />}
                                                iconOnly
                                                hideCaret
                                                open={showSideNavOverlay}
                                                onShow={() => setShowSideNavOverlay(true)}
                                                onClose={() => setShowSideNavOverlay(false)}
                                                isOpusUser={isOpusUser}
                                                isCompleteTheme={isCompleteTheme(themeName)}
                                            >
                                                <ExpandedSideNav
                                                    inOverlay
                                                    reducedWidth={hasFlexibleLayoutSidePanel || hasEXTJS}
                                                    id="sapCnqrSideNavContainer"
                                                    menuData={adjustedMenuData}
                                                    activePathArray={activePathArray}
                                                    menuButtonRef={menuButtonRef}
                                                    style={{
                                                        height: `calc(100vh - ${
                                                            headerBottom + extraHeight
                                                        }px)`,
                                                    }}
                                                    onCloseButtonClick={() => setShowSideNavOverlay(false)}
                                                />
                                            </MenuItemToggle>
                                        </>
                                    )}
                                    {homeMenuItem && (
                                        <MenuItem
                                            {...homeMenuItem}
                                            tooltipMessage={formatter.formattedMessage({
                                                id: 'CoreUI.navigateToHome',
                                            })}
                                            doNotDescribe // prevent double announcement of "S A P Concur Logo"
                                            styles={styles}
                                            cssBlock={CSS_BLOCK}
                                            className={classnames(`${CSS_BLOCK}__listitem--home`)}
                                            isActive={false} // home menu item is never shown as active
                                            key="dashboard"
                                            labelId={`${
                                                homeMenuItem?.labelId || ''
                                            } ${homeLogoDescriptionId}`.trim()}
                                            menuButtonRef={menuButtonRef}
                                            data-analytics-json={getSidenavItemAnalyticsJson('Company Logo')}
                                        >
                                            {navLogo}
                                        </MenuItem>
                                    )}
                                </ul>
                                {/* we have to render this container, hidden in the shellbar,
                                    so that when the TrustArc script is added to the page with the useEffect above -
                                    it has an element to inject its contents into. */}
                                {formatter?.preferences?.lang ? <div hidden id="teconsent" className={classnames(`${CSS_BLOCK}__hidden-trustarc-container`)} /> : null}
                                {(isLocalizationUser || helpMenuItem || profileMenuItem) && (
                                    <ul
                                        className={classnames(
                                            `${CSS_BLOCK}__menu`,
                                            `${CSS_BLOCK}__menu--utility`,
                                        )}
                                        ref={menuButtonRef}
                                    >
                                        {isLocalizationUser && (
                                            <TranslationToolMenuItem
                                                onLoad={setTranslationToolLoaded}
                                            />
                                        )}
                                        <DigitalAssistantMenuItem />
                                        <MessageCenterMenuItem />
                                        <HelpPortalMenuItem
                                            menuData={adjustedMenuData}
                                            onLoad={setHelpPortalLoaded}
                                        />
                                        {profileMenuItem && (
                                            <>
                                                <MenuItemToggle
                                                    buttonClassName={
                                                        isActingForOthers
                                                            ? classnames(
                                                                `${CSS_BLOCK}__button--acting-as`,
                                                            )
                                                            : classnames(
                                                                `${CSS_BLOCK}__button--avatar`,
                                                            )
                                                    }
                                                    buttonProps={{
                                                        'data-test': profileMenuItem.id
                                                            ? `menu-${lowerCase(
                                                                profileMenuItem.id,
                                                            )}`
                                                            : null,
                                                        innerTextClasses: classnames(`${CSS_BLOCK}__profile-description`),
                                                    }}
                                                    overlayProps={profileOverlayProps}
                                                    styles={styles}
                                                    cssBlock={CSS_BLOCK}
                                                    content={generateProfileButton()}
                                                    tooltipMessage={isActingForOthers ? `${getProfileDisplayText({
                                                        actingAs,
                                                        formatter,
                                                        employeeDisplayName,
                                                        shortNames: true,
                                                    })}, ${formatter.formattedMessage({
                                                        id: 'CoreUI.profile.openProfileMenu',
                                                    })}` : formatter.formattedMessage({ id: 'CoreUI.profile.openProfileMenu' })}
                                                    doNotDescribe // prevent double announcement of "Open Profile"
                                                    hideCaret
                                                    iconOnly={!isActingForOthers}
                                                    popperPlacement="bottom-end"
                                                    open={showUserProfileMenu}
                                                    onShow={() => setShowUserProfileMenu(true)}
                                                    onClose={() => setShowUserProfileMenu(false)}
                                                >
                                                    <UserProfileMenu
                                                        userProfileMenuProps={
                                                            otherUserProfileMenuProps
                                                        }
                                                        oshCacheIdentifier={oshCacheIdentifier}
                                                        styles={styles}
                                                        actingAs={actingAs}
                                                        baseUrl={baseUrl}
                                                        lang={lang}
                                                        onEndLoading={handleEndLoading}
                                                        onStartLoading={handleStartLoading}
                                                        profileUrl={profileMenuItem.url}
                                                        errorComp={fallbackComp}
                                                        actingAsQuickHelpRef={profileQuickHelpRef}
                                                        actingAsUserDropdownRef={
                                                            profileUserDropdownRef
                                                        }
                                                        showAboutDialogCallback={(show) => setShowAboutDialog(show)}
                                                        isOpusUser={isOpusUser}
                                                        isCompleteTheme={isCompleteTheme(themeName)}
                                                    />
                                                </MenuItemToggle>
                                                <Spinner
                                                    message={formatter.formattedMessage({
                                                        id: 'CoreUI.switchingActingAsView',
                                                    })}
                                                    type="fullScreen"
                                                    visible={showSpinner}
                                                />
                                            </>
                                        )}
                                    </ul>
                                )}
                            </NavMenu.Bar>
                        </div>
                    </>
                ) : (
                    <span
                        className={classnames(`${CSS_BLOCK}__header`, {
                            [`${CSS_BLOCK}__header--signin`]: !isLoggedIn,
                        })}
                    >
                        <span className={classnames(`${CSS_BLOCK}__brand`, {
                            [`${CSS_BLOCK}__brand--signin`]: !isLoggedIn,
                        })}
                        >
                            <Logo
                                cssBlock={CSS_BLOCK}
                                styles={styles}
                                className={homeIconClasses}
                                hideTitle
                                isOpusUser={isOpusUser}
                            />
                        </span>
                        {showLanguageDropdown && (
                            <LanguageDropdown styles={styles} cssBlock={CSS_BLOCK} />
                        )}
                    </span>
                )}
                {isLoggedIn
                    && showSideNavContent
                    && (!sideNavOverlay || sideNavCollapsedOverlay) && (
                    <div
                        id="sapCnqrSideNavContainer"
                        className={classnames(`${CSS_BLOCK}__side-nav`, {
                            [`${CSS_BLOCK}__side-nav--expanded`]: sideNavExpanded,
                        })}
                        style={{
                            top: `calc(${headerBottom} + ${testEntityBannerBottom})px`,
                            height: `calc(100vh - ${headerBottom + extraHeight}px)`,
                        }}
                    >
                        {sideNavExpanded && !sideNavCollapsedOverlay ? (
                            <ExpandedSideNav
                                menuButtonRef={menuButtonRef}
                                menuData={adjustedMenuData}
                                activePathArray={activePathArray}
                                onCollapse={() => {
                                    setSideNavExpanded(false);
                                }}
                            />
                        ) : (
                            <SideNav
                                menuItems={adjustedMenuData}
                                activePathArray={activePathArray}
                            />
                        )}
                    </div>
                )}
            </div>
            <AboutDialog
                show={showAboutDialog}
                formatter={formatter}
                partnerLogo={partnerLogo}
                lastSigninDateString={lastSigninDateString}
                isProfessional={isProfessional}
                isStandard={isStandard}
                showAboutDialogCallback={(show) => setShowAboutDialog(show)}
                isOpusUser={isOpusUser}
            />
        </>
    );
};

Shellbar.displayName = 'Shellbar';

Shellbar.cssBlock = CSS_BLOCK;

Shellbar.propTypes = {
    actingAs: PropTypes.shape({
        allUsers: PropTypes.bool,
        companyName: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.string),
        otherUserName: PropTypes.string,
    }),
    baseUrl: PropTypes.string,
    menuData: PropTypes.shape({
        homeMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        applicationMenuItems: PropTypes.arrayOf(PropTypes.shape(MenuItem.apiPropTypes)),
        profileMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        adminMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        helpMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        linksMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        testDriveMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        localizationToolMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        subscribeMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        supportMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        pageMenuItems: PropTypes.arrayOf(PropTypes.shape(MenuItem.apiPropTypes)),
        noMenuDataFallback: PropTypes.node,
    }),
    employeeName: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        middleName: PropTypes.string,
        preferredName: PropTypes.string,
    }),
    className: PropTypes.string,
    lang: PropTypes.string,
    isGov: PropTypes.bool,
    isLoggedIn: PropTypes.bool,
    logoutUser: PropTypes.func,
    onEndSession: PropTypes.func,
    onStartSession: PropTypes.func,
    showLanguageDropdown: PropTypes.bool,
    stretchContent: PropTypes.bool,
    useFullWidth: PropTypes.bool,
    useSmallViewportBreakpoints: PropTypes.bool,
    userProfileMenuProps: PropTypes.object,
    aboutDemo: PropTypes.bool,
    isOpusUser: PropTypes.bool,
    isNuiHome: PropTypes.bool,
    cdsURL: PropTypes.string,
    configOptions: PropTypes.object,
    brandingLogoUrl: PropTypes.string,
    brandingLogoData: PropTypes.string,
    companyName: PropTypes.string,
    oshCacheIdentifier: PropTypes.string,
    demo: PropTypes.bool,
};

export default compose(withFormatter, withErrorBoundary)(Shellbar);
